import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from 'util/UserContext';
import api from 'config/axiosConfig';
import { Info, Loader2 } from 'lucide-react'; // Using lucide-react icons
import Modal from 'ui/Modal'; // Adjust the import path as necessary
import { Issue } from 'util/constants';
import IssueSearch, { Suggestion } from 'components/Issues/IssueSearch';
import InputField from 'ui/InputField';
import { getWikipediaSummary } from 'util/wikiUtils';
import { createIssueFromWikipedia, createCompoundIssue } from 'util/issueCreationUtils';
import { apiRoutes } from "util/apiRoutes";
import { addIssueSubmissionSchema, wikipediaUrlSchema } from "lib/validation/contentSchemas";
import { normalizeApiError } from "util/normalizeApiError";
import { logger } from "lib/logger";

interface Props {
    open: boolean;
    onClose: (value: boolean) => void;
    prepopulateData?: { name: string; wikipediaLink: string; cname: string; subject_properties: any; } | null;
}

const AddIssue: React.FC<Props> = ({ open, onClose, prepopulateData }) => {
    const { currentUser, setShowAuthModal } = useUser();
    const navigate = useNavigate();
    const [prepopulatedData, setPrepopulatedData] = useState<Props['prepopulateData']>(prepopulateData);
    const [wikipediaLink, setWikipediaLink] = useState<string>(prepopulateData?.wikipediaLink || '');
    const [topicWikipediaLink, setTopicWikipediaLink] = useState<string>('');
    const [selectedInfluencer, setSelectedInfluencer] = useState<Suggestion | null>(prepopulateData?.subject_properties ? {
        type: 'wiki',
        name: prepopulateData.name,
        link: prepopulateData.wikipediaLink,
        cname: prepopulateData.cname,
        subject_properties: prepopulateData.subject_properties,
        thumbnail: prepopulateData.subject_properties?.thumbnail?.source || ''
    } : null);
    const [selectedInfluenced, setSelectedInfluenced] = useState<Suggestion | null>(null);
    const [error, setError] = useState<string>('');
    const [submitEnabled, setSubmitEnabled] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isCreatingIssue, setIsCreatingIssue] = useState<boolean>(false);
    const [existingIssueLink, setExistingIssueLink] = useState<string>('');
    const [isChecking, setIsChecking] = useState<boolean>(false);
    const [showInfluencerWikipediaField, setShowInfluencerWikipediaField] = useState<boolean>(false);
    const [showInfluencedWikipediaField, setShowInfluencedWikipediaField] = useState<boolean>(false);

    // Effect to handle prepopulated data
    useEffect(() => {
        if (open && prepopulateData) {
            setPrepopulatedData(prepopulateData);
            setWikipediaLink(prepopulatedData?.wikipediaLink || '');
            setSelectedInfluencer({
                type: 'wiki',
                name: prepopulatedData?.name || '',
                link: prepopulatedData?.wikipediaLink || '',
                cname: prepopulatedData?.cname || '',
                subject_properties: prepopulatedData?.subject_properties || {},
                thumbnail: prepopulatedData?.subject_properties?.thumbnail?.source || ''
            });
            setSelectedInfluenced(null);
        } else {
            // Clear all state when modal is closed
            setPrepopulatedData(null);
            setWikipediaLink('');
            setTopicWikipediaLink('');
            setSelectedInfluencer(null);
            setSelectedInfluenced(null);
            setError('');
            setSubmitEnabled(false);
            setIsSubmitting(false);
            setIsCreatingIssue(false);
            setExistingIssueLink('');
            setIsChecking(false);
            setShowInfluencerWikipediaField(false);
            setShowInfluencedWikipediaField(false);
        }
    }, [open, prepopulateData, prepopulatedData]);

    const validateWikipediaLink = async (link: string, isInfluencedLink: boolean = false) => {
        setError('');
        const linkValidation = wikipediaUrlSchema.safeParse(link);
        if (linkValidation.success) {
            try {
                const title = decodeURIComponent(link.split('/wiki/')[1]);
                const data = await getWikipediaSummary(title);
                if (!data) {
                    throw new Error('Wikipedia page not found');
                }
                const canonicalTitle = data.canonicalTitle;
                setIsChecking(true);
                
                // Check if this exact issue already exists
                const issueResponse = await api.get(apiRoutes.issues.root, {
                    params: { search: canonicalTitle }
                });
                let existingIssue = issueResponse.data.find(
                    (issue: Issue) => issue.cname === canonicalTitle
                );
                
                if (existingIssue) {
                    const issueUrl = `/issue/${existingIssue.cname}`;
                    setExistingIssueLink(issueUrl);
                    
                    if (isInfluencedLink) {
                        setSelectedInfluenced({...existingIssue, type: 'issue'});
                    } else {
                        setSelectedInfluencer({...existingIssue, type: 'issue'});
                    }
                } else {           
                    if (isInfluencedLink) {
                        setTopicWikipediaLink(link);
                        setSelectedInfluenced({
                            type: 'wiki',
                            name: canonicalTitle,
                            link,
                            cname: canonicalTitle,
                            subject_properties: data,
                            thumbnail: data.thumbnail || ''
                        });
                    } else {
                        setWikipediaLink(link);
                        setSelectedInfluencer({
                            type: 'wiki',
                            name: canonicalTitle,
                            link,
                            cname: canonicalTitle,
                            subject_properties: data,
                            thumbnail: data.thumbnail || ''
                        });
                    }
                }
            } catch (error) {
                logger.error("Failed to fetch canonical Wikipedia title", { error });
                setError('Error fetching Wikipedia page data');
            } finally {
                setIsChecking(false);
            }
        } else {
            setError(linkValidation.error.issues[0]?.message || 'Invalid Wikipedia link. Example: https://en.wikipedia.org/wiki/Crime');
            setIsChecking(false);
        }
    };

    const handleWikipediaLinkChange = async (event: React.ChangeEvent<HTMLInputElement>, isInfluencedLink: boolean = false) => {
        const value = event.target.value;
        if (isInfluencedLink) {
            setTopicWikipediaLink(value);
            if (value) await validateWikipediaLink(value, true);
        } else {
            setWikipediaLink(value);
            if (value) await validateWikipediaLink(value);
        }
    };

    const handleSelectInfluencer = (suggestion: Suggestion) => {
        setSelectedInfluencer(suggestion);
        if (suggestion.type === 'wiki') {
            setWikipediaLink(suggestion.link || '');
        } else {
            setWikipediaLink(suggestion.wikipedia_link || '');
        }
    };

    const handleSelectInfluenced = (suggestion: Suggestion) => {
        setSelectedInfluenced(suggestion);
        if (suggestion.type === 'wiki') {
            setTopicWikipediaLink(suggestion.link || '');
        } else {
            setTopicWikipediaLink(suggestion.wikipedia_link || '');
        }
    };

    useEffect(() => {
        const isValidSubmit = () => {
            setError('');
            // Must have a valid influencer
            if (!selectedInfluencer) return false;

            const parsed = addIssueSubmissionSchema.safeParse({
                influencerCname: selectedInfluencer.cname,
                influencedCname: selectedInfluenced?.cname,
                showInfluencerWikipediaField,
                showInfluencedWikipediaField,
                wikipediaLink,
                topicWikipediaLink,
            });
            if (!parsed.success) {
                setError(parsed.error.issues[0]?.message || "Invalid issue submission data.");
                return false;
            }

            if (selectedInfluencer.type === 'issue' && !selectedInfluenced) {
                setError('The influencer already exists as an issue; add an influenced issue to create a relationship.');
                return false;
            }

            // Influencer and influenced must be different
            if (selectedInfluenced && selectedInfluenced.cname === selectedInfluencer.cname) {
                setError('The influencer and influenced cannot be the same.');
                return false;
            }

            // Not currently submitting or checking
            if (isSubmitting || isCreatingIssue || isChecking) return false;

            // No existing issue error
            if (error) return false;

            return true;
        };

        setSubmitEnabled(isValidSubmit());
    }, [selectedInfluencer, selectedInfluenced, wikipediaLink, topicWikipediaLink, isSubmitting, isCreatingIssue, isChecking, error, showInfluencerWikipediaField, showInfluencedWikipediaField]);

    const handleInfluencerChipClose = () => {
        setSelectedInfluencer(null);
        setWikipediaLink('');
    };

    const handleInfluencedChipClose = () => {
        setSelectedInfluenced(null);
        setTopicWikipediaLink('');
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!currentUser) {
            setShowAuthModal(true);
            return;
        }

        if (!submitEnabled || !selectedInfluencer) {
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            let influencerIssue: Issue | null = null;
            let influencedIssue: Issue | null = null;

            // Create or get influencer issue
            if (selectedInfluencer.type === 'wiki') {
                setIsCreatingIssue(true);
                influencerIssue = await createIssueFromWikipedia(selectedInfluencer);
            } else {
                const response = await api.get(apiRoutes.issues.byId(selectedInfluencer.id));
                influencerIssue = response.data;
            }

            if (!influencerIssue) {
                throw new Error('Failed to create or fetch influencer issue');
            }

            // If there's an influenced issue, create or get it
            if (selectedInfluenced) {
                if (selectedInfluenced.type === 'wiki') {
                    influencedIssue = await createIssueFromWikipedia(selectedInfluenced);
                } else {
                    const response = await api.get(apiRoutes.issues.byId(selectedInfluenced.id));
                    influencedIssue = response.data;
                }

                if (!influencedIssue) {
                    throw new Error('Failed to create or fetch influenced issue');
                }

                // Create the influence relationship (compound issue)
                const compoundIssue = await createCompoundIssue(
                    influencerIssue.cname,
                    influencedIssue.cname,
                    influencerIssue.wikipedia_link || '',
                    influencedIssue.wikipedia_link || '',
                    influencerIssue.properties?.subject_properties,
                    influencedIssue.properties?.subject_properties
                );
                handleCloseModal();
                navigate(`/issue/${compoundIssue.cname}?upvoted=true`);
            } else {
                handleCloseModal();
                navigate(`/issue/${influencerIssue.cname}?upvoted=true`);
            }
        } catch (error: unknown) {
            logger.error("Failed to create issue", { error });
            setError(normalizeApiError(error, 'Failed to create issue. Please try again later.'));
        } finally {
            setIsSubmitting(false);
            setIsCreatingIssue(false);
        }
    };

    const handleCloseModal = () => {
        onClose(false);
        setWikipediaLink('');
        setTopicWikipediaLink('');
        setSelectedInfluencer(null);
        setSelectedInfluenced(null);
        setError('');
        setSubmitEnabled(false);
        setIsSubmitting(false);
        setIsCreatingIssue(false);
        setExistingIssueLink('');
        setIsChecking(false);
        setShowInfluencerWikipediaField(false);
        setShowInfluencedWikipediaField(false);
    };

    const footer = (
        <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex sm:flex-row sm:justify-end sm:gap-x-2 py-2">
            <button
                type="button"
                onClick={handleCloseModal}
                className="btn-secondary w-full sm:w-fit py-2 px-4"
            >
                Cancel
            </button>
            <button
                type="submit"
                onClick={handleSubmit}
                disabled={isSubmitting || !submitEnabled}
                className={`w-full sm:w-fit py-2 px-4 ${
                    !submitEnabled ? 'btn-primary-disabled' : 'btn-primary'
                } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
                {isSubmitting ? <Loader2 className="size-4 animate-spin" /> : isCreatingIssue ? <Loader2 className="size-4 animate-spin" /> : 'Submit'}
            </button>
        </div>
    );

    return (
        <Modal isOpen={open} onClose={handleCloseModal} title="Add issue" footer={footer} stickyFooter>
            <div className="flex flex-col w-full sm:h-auto overflow-y-scroll sm:overflow-y-auto space-y-4">
                <div className="flex flex-col flex-grow space-y-4">
                    <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                    Search and select an issue: anything that affects society—directly or indirectly through influence.
                    </p>

                    <form onSubmit={handleSubmit} className="flex flex-col flex-grow space-y-4">
                        {/* Influencer Section */}
                        <div className="relative flex flex-row gap-2 items-center mb-4 mx-2">
                            <div className="w-full border-t border-eoe-border-main dark:border-eoe-dark-border-main" />
                            <div className="mt-0 relative flex flex-row flex-nowrap flex-shrink-0 justify-center text-sm">
                                <h3 className="font-semibold text-eoe-text-heading dark:text-eoe-dark-text-heading">Influencer<span className="text-red-500">&nbsp;*</span></h3>
                            </div>
                            <div className="w-full border-t border-eoe-border-main dark:border-eoe-dark-border-main" />
                        </div>

                        <div className="form-control">
                            <IssueSearch
                                inputType="add"
                                placeholder="Influencer"
                                className="w-full"
                                autoFocus={true}
                                fieldRequired={true}
                                prepopulateData={selectedInfluencer as any}
                                onSelect={handleSelectInfluencer}
                                onEntityChipClose={handleInfluencerChipClose}
                                excludeCompoundIssues={true}
                            />
                        </div>

                        <div className="grid grid-flow-col-dense gap-3 items-center justify-between sm:justify-start">
                            <label htmlFor="influencer-wiki" className="block text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub pl-1">
                                Use Wikipedia link instead
                            </label>
                            <input
                                type="checkbox"
                                id="influencer-wiki"
                                checked={showInfluencerWikipediaField}
                                onChange={(e) => setShowInfluencerWikipediaField(e.target.checked)}
                                className="toggle-switch-input"
                            />
                        </div>

                        {showInfluencerWikipediaField && (
                            <div className="form-control">
                                <InputField
                                    id="wikipedia-link"
                                    type="url"
                                    name="wikipedia-link"
                                    labelText="Wikipedia URL"
                                    value={wikipediaLink}
                                    onChange={(e) => handleWikipediaLinkChange(e, false)}
                                    fieldPlaceholder="https://en.wikipedia.org/wiki/Example"
                                    autoComplete="off"
                                    spellCheck={false}
                                />
                            </div>
                        )}

                        {/* Influenced Section */}
                        <div className="modal-sub-section">
                            <div className="relative flex flex-row gap-2 items-center mb-4 mx-2">
                                <div className="w-full border-t border-eoe-border-main dark:border-eoe-dark-border-main" />
                                <div className="mt-0 relative flex flex-row flex-nowrap flex-shrink-0 justify-center text-sm">
                                    <h3 className="font-semibold text-eoe-text-heading dark:text-eoe-dark-text-heading">Influenced</h3>
                                </div>
                                <div className="w-full border-t border-eoe-border-main dark:border-eoe-dark-border-main" />
                            </div>

                            <div className="form-control">
                                <IssueSearch
                                    inputType="add"
                                    placeholder="Influenced (optional)"
                                    className="w-full"
                                    autoFocus={false}
                                    fieldRequired={false}
                                    prepopulateData={selectedInfluenced as any}
                                    onSelect={handleSelectInfluenced}
                                    onEntityChipClose={handleInfluencedChipClose}
                                    excludeCompoundIssues={true}
                                />
                            </div>

                            <div className="grid grid-flow-col-dense gap-3 items-center justify-between sm:justify-start pt-3">
                                <label htmlFor="influenced-wiki" className="block text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub pl-1">
                                    Use Wikipedia link instead
                                </label>
                                <input
                                    type="checkbox"
                                    id="influenced-wiki"
                                    checked={showInfluencedWikipediaField}
                                    onChange={(e) => setShowInfluencedWikipediaField(e.target.checked)}
                                    className="toggle-switch-input"
                                />
                            </div>

                            {showInfluencedWikipediaField && (
                                <div className="form-control mt-4">
                                    <InputField
                                        id="topic-wikipedia-link"
                                        type="url"
                                        name="topic-wikipedia-link"
                                        labelText="Wikipedia URL"
                                        value={topicWikipediaLink}
                                        onChange={(e) => handleWikipediaLinkChange(e, true)}
                                        fieldPlaceholder="https://en.wikipedia.org/wiki/Different_Example"
                                        autoComplete="off"
                                        spellCheck={false}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="text-warning-red dark:text-warning-red-dark text-sm flex items-center">
                                <Info className="mr-1 size-4 place-self-start" />
                                {existingIssueLink ? (
                                    <div className="flex flex-col">
                                        <span>{error}</span>
                                        <a href={existingIssueLink} className="internal-link underline hover:no-underline">
                                            View existing issue: {selectedInfluencer?.name || selectedInfluenced?.name}
                                        </a>
                                    </div>
                                ) : (
                                    <span>{error}</span>
                                )}
                            </div>
                        )}
                    </form>
                </div>

                {/* Action buttons moved to Modal footer */}
            </div>
        </Modal>
    );
};

export default AddIssue;